import React, { useState, useEffect } from 'react'
import { Box, IconButton, Modal, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import TextField from '@mui/material/TextField'
import calendarIcon from '../../assets/calendar/calendar-newEvent.svg'
import close from '../../assets/table_icons/delete.svg'
import { FormStatByDate } from '../../modules/forms/api/AnalyticsApi'
import { ChartView, DateFilter, toISODate, toChartData, lastWeekRange } from './FormStatsChart'
import styles from './FormStatModal.module.css'

interface FormStatModalProps {
  open: boolean
  onClose: () => void
  title: string
  userId: string
  fetchStats: (userId: string, start: string, end: string) => Promise<FormStatByDate[]>
  optionLabels: Record<string, string>
}

const FormStatModal = ({
  open,
  onClose,
  title,
  userId,
  fetchStats,
  optionLabels,
}: FormStatModalProps) => {
  const { t } = useTranslation()
  const [date, setDate] = useState<DateFilter>(lastWeekRange())
  const [error, setError] = useState('')
  const [chartData, setChartData] = useState<{ labels: string[]; datasets: any[] }>({
    labels: [],
    datasets: [],
  })
  const [isLoading, setIsLoading] = useState(false)

  const loadData = (d: DateFilter) => {
    setIsLoading(true)
    fetchStats(userId, toISODate(d.startDate), toISODate(d.finishDate))
      .then((s) => setChartData(toChartData(s, optionLabels)))
      .catch(() => setChartData({ labels: [], datasets: [] }))
      .finally(() => setIsLoading(false))
  }

  useEffect(() => {
    if (open) loadData(date)
  }, [open])

  const handleStartDateChange = (e: any) => {
    if (!e) return
    const newDate = { ...date, startDate: e.toDate() }
    setDate(newDate)
    loadData(newDate)
  }

  const handleFinishDateChange = (e: any) => {
    if (!e) return
    const newDate = { ...date, finishDate: e.toDate() }
    setDate(newDate)
    loadData(newDate)
  }

  return (
    <Modal open={open} onClose={onClose}>
      <Box className={styles.overlay}>
        <Box className={styles.container}>
          <Box className={styles.header}>
            <Box sx={{ width: 180 }}>
              <Typography className={styles.dateLabel}>{t('startDate')}</Typography>
              <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={'es'}>
                <DatePicker
                  key={'startDate'}
                  inputFormat={'DD/MM/YYYY'}
                  onChange={(e) => handleStartDateChange(e)}
                  value={date.startDate}
                  label={''}
                  onError={(reason) => {
                    if (reason) setError(t('invalidDateMessage'))
                  }}
                  renderInput={(params) => (
                    <TextField
                      size={'small'}
                      sx={{
                        '& fieldset': {
                          borderRadius: 32,
                          border: '1px solid var(--gray-soft)',
                        },
                        '& .MuiOutlinedInput-root': {
                          border: 'none',
                          '&.Mui-focused fieldset': {
                            borderColor: 'var(--green-light)',
                          },
                        },
                      }}
                      helperText={params.error && error}
                      {...params}
                    />
                  )}
                  components={{
                    OpenPickerIcon: () => (
                      <img src={calendarIcon} alt="calendar" style={{ marginRight: 8 }} />
                    ),
                  }}
                />
              </LocalizationProvider>
            </Box>
            <Box sx={{ width: 180 }}>
              <Typography className={styles.dateLabel}>{t('finishDate')}</Typography>
              <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={'es'}>
                <DatePicker
                  key={'finishDate'}
                  inputFormat={'DD/MM/YYYY'}
                  onChange={(e) => handleFinishDateChange(e)}
                  value={date.finishDate}
                  label={''}
                  onError={(reason) => {
                    if (reason) setError(t('invalidDateMessage'))
                  }}
                  renderInput={(params) => (
                    <TextField
                      size={'small'}
                      sx={{
                        '& fieldset': {
                          borderRadius: 32,
                          border: '1px solid var(--gray-soft)',
                        },
                        '& .MuiOutlinedInput-root': {
                          border: 'none',
                          '&.Mui-focused fieldset': {
                            borderColor: 'var(--green-light)',
                          },
                        },
                      }}
                      helperText={params.error && error}
                      {...params}
                    />
                  )}
                  components={{
                    OpenPickerIcon: () => (
                      <img src={calendarIcon} alt="calendar" style={{ marginRight: 8 }} />
                    ),
                  }}
                />
              </LocalizationProvider>
            </Box>
            <Box className={styles.closeButton}>
              <IconButton onClick={onClose} size="small">
                <img src={close} alt="close" style={{ width: 24, height: 24 }} />
              </IconButton>
            </Box>
          </Box>

          <Typography variant="h6" className={styles.title}>
            {title}
          </Typography>

          <ChartView data={chartData} isLoading={isLoading} showDetails={true} />
        </Box>
      </Box>
    </Modal>
  )
}

export default FormStatModal
